import { parseDate } from '@quromedical/utils';
import { differenceInDays, startOfDay } from 'date-fns';

import { isPeriodComplete } from '../utils';

type DateType = string | number | Date;

/**
 * Create a date string of the form YYYY-MM-DD
 */
export const createFhirDate = (date: Date): string =>
  date.toISOString().split('T')[0];

// NOTE: This is using keyword `function` instead of `const` to allow for overloading
export function createOptionalFhirDate(date: DateType): string;
export function createOptionalFhirDate(date: undefined): undefined;
export function createOptionalFhirDate(date?: DateType): string | undefined;
export function createOptionalFhirDate(date?: DateType): string | undefined {
  if (!date) {
    return undefined;
  }

  const d = new Date(date);

  return createFhirDate(d);
}

/**
 * Creates an ISO Date formatted string
 */
export const createFhirDateTime = (date: Date): string => date.toISOString();

export interface PeriodInProgress {
  start: string;
}

export interface PeriodComplete {
  start: string;
  end: string;
}

/**
 * Period type is aligned to be a more specific definition of the `fhir4.Period` type
 */
export type Period = PeriodInProgress | PeriodComplete;

/**
 * A period can be mapped to a value if the `start` is defined, the `end` may optionally be defined
 */
export const mapFhirToModelPeriod = (
  fhir?: fhir4.Period,
): Period | undefined => {
  if (!fhir) {
    return undefined;
  }

  const { start, end } = fhir;

  if (!start) {
    return undefined;
  }

  return {
    start,
    end,
  };
};

/**
 * Validates that a given date is within period. If hte period is not complete,
 * the end date is ignored and the date is is within the period if it is after or
 * equal to the the start date
 * @param date
 * @param period
 * @returns
 */
export const isDateWithinPeriod = (date: Date, period: Period): boolean => {
  const completePeriod = isPeriodComplete(period);

  const { start, end } = completePeriod
    ? period
    : { ...period, end: undefined };

  const startDate = new Date(start);
  const endDate = end ? new Date(end) : undefined;

  return startDate <= date && (!endDate || endDate >= date);
};

export const getDurationInDays = (
  isoDate?: string | number,
  relativeDate: string | number = new Date().toISOString(),
): number | undefined => {
  const parsed = parseDate(isoDate);
  const relative = parseDate(relativeDate);

  const bothValid = parsed && relative;

  if (!bothValid) {
    return undefined;
  }
  const days = differenceInDays(startOfDay(relative), startOfDay(parsed));

  return days;
};
